import { Injectable } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { UIAlert, UIConfirm, UIScreenViewModel } from '@edm-sdui/sdui';
import { take } from 'rxjs/operators';
import {
  ModalDialogButton,
  ModalDialogComponent,
} from '../../shared/dialogs/modal-dialog/modal-dialog.component';
import { GeneralService } from './general.service';
import { PaywallComponent } from '../../timeline/lessons/paywall/paywall.component';

@Injectable({ providedIn: 'root' })
export class SduiAlertService {
  // Mesmo diálogo que a tela de módulos usa para aula em espera; só a largura é maior
  // (padrão do ngb, 500px) — no 'sm' o texto do alerta quebrava em três linhas.
  private static readonly MODAL_OPTIONS: NgbModalOptions = {
    backdrop: 'static',
    keyboard: false,
    container: 'nb-layout',
    windowClass: 'about-modal-wrapper',
    backdropClass: 'about-modal-backdrop',
  };

  // Mesmas opções que a tela de módulos usa no paywall — o `lg` é o que dá largura ao iframe.
  private static readonly PAYWALL_OPTIONS: NgbModalOptions = {
    backdrop: 'static',
    keyboard: false,
    size: 'lg',
    container: 'nb-layout',
  };

  constructor(
    private modal: NgbModal,
    private generalService: GeneralService,
    private viewModel: UIScreenViewModel,
  ) {}

  // Conteúdo travado por compra: o SDUI manda a URL do checkout e quem abre é o mesmo
  // PaywallComponent do produto tradicional.
  showPaywall(url: string): void {
    if (!url) {
      return;
    }

    const activeModal: NgbModalRef = this.modal.open(
      PaywallComponent,
      SduiAlertService.PAYWALL_OPTIONS,
    );
    activeModal.componentInstance.model = url;

    // O item bloqueado não traz o id da aula; a URL do checkout traz o do produto, que é
    // o que o paywall usa para registrar a compra.
    const productId: string | null = this.productIdFrom(url);
    if (productId) {
      activeModal.componentInstance.contentId = productId;
      activeModal.componentInstance.contentType = 'product';
    }

    activeModal.result.then(
      (data: string): void => {
        if (data === 'reload') {
          this.reloadCurrentScreen();
        }
      },
      (): void => {},
    );
  }

  private productIdFrom(url: string): string | null {
    try {
      return new URL(url).searchParams.get('product_id');
    } catch {
      return null;
    }
  }

  // Comprou: a cena precisa voltar sem o cadeado. Recarga silenciosa, a mesma da
  // reexibição — a trilha manda always_reload.
  private reloadCurrentScreen(): void {
    this.viewModel.currentUrl$
      .pipe(take(1))
      .subscribe((url: string | null): void => {
        this.viewModel.reloadIfNeeded(url);
      });
  }

  show(alert: UIAlert): void {
    if (!alert?.message && !alert?.title) {
      return;
    }

    const dismissLabel: string =
      alert.dismissLabel ||
      this.generalService.getTranslate('timeline.lessons.dialog-waiting.closeLabel');
    const buttons: ModalDialogButton[] = [
      { label: dismissLabel, style: 'primary', action: 'dismiss' },
    ];

    const activeModal: NgbModalRef = this.modal.open(
      ModalDialogComponent,
      SduiAlertService.MODAL_OPTIONS,
    );
    activeModal.componentInstance.icon = 'schedule';
    activeModal.componentInstance.title = alert.title ?? '';
    activeModal.componentInstance.message = this.plainText(alert.message);
    activeModal.componentInstance.buttons = buttons;
    activeModal.result.then(
      (): void => {},
      (): void => {},
    );
  }

  // Ação crítica (reiniciar produto): dois botões, no mesmo formato que a tela de
  // módulos já usa. Resolve true só no confirmar; fechar por fora conta como cancelar.
  confirm(confirmation: UIConfirm): Promise<boolean> {
    const confirmLabel: string =
      confirmation?.confirmLabel ||
      this.generalService.getTranslate('timeline.modules.confirmRestartConfirm');
    const cancelLabel: string =
      confirmation?.cancelLabel ||
      this.generalService.getTranslate('timeline.modules.confirmRestartCancel');
    const buttons: ModalDialogButton[] = [
      { label: cancelLabel, style: 'outline', action: 'dismiss' },
      { label: confirmLabel, style: 'primary', action: 'confirm' },
    ];

    const activeModal: NgbModalRef = this.modal.open(
      ModalDialogComponent,
      SduiAlertService.MODAL_OPTIONS,
    );
    activeModal.componentInstance.icon = 'restart_alt';
    activeModal.componentInstance.title = confirmation?.title ?? '';
    activeModal.componentInstance.message = this.plainText(confirmation?.message);
    activeModal.componentInstance.buttons = buttons;

    return activeModal.result.then(
      (): boolean => true,
      (): boolean => false,
    );
  }

  // O texto pode vir com HTML do cadastro; o diálogo renderiza como texto puro.
  private plainText(message: string | null | undefined): string {
    return (message ?? '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
}
